import React, { useState, useEffect, useCallback } from 'react';
import BuildingSvgWrapper from './BuildingSvgWrapper';
import DataSourceEditor from './DataSourceEditor';
import ThresholdEditor from './ThresholdEditor';
import TooltipOverlay from './TooltipOverlay';
import ApiDataViewer from './ApiDataViewer';
import ProjectPersistenceModal from './ProjectPersistenceModal';
import SearchableSelect from './SearchableSelect';

// --- SVG to JSX Helpers ---

const toCamelCase = (str) => {
  return str.replace(/([-_][a-z])/ig, ($1) => {
    return $1.toUpperCase()
      .replace('-', '')
      .replace('_', '');
  });
};

const convertStyle = (styleString) => {
  if (!styleString) return null;
  const obj = {};
  styleString.split(';').forEach(pair => {
    const [key, value] = pair.split(':');
    if (key && value) {
      obj[toCamelCase(key.trim())] = value.trim();
    }
  });
  return obj;
};

// Comprehensive React SVG Attribute Map
const attrMap = {
  'accent-height': 'accentHeight',
  'alignment-baseline': 'alignmentBaseline',
  'arabic-form': 'arabicForm',
  'baseline-shift': 'baselineShift',
  'cap-height': 'capHeight',
  'class': 'className',
  'clip-path': 'clipPath',
  'clip-rule': 'clipRule',
  'color-interpolation': 'colorInterpolation',
  'color-interpolation-filters': 'colorInterpolationFilters',
  'color-profile': 'colorProfile',
  'color-rendering': 'colorRendering',
  'dominant-baseline': 'dominantBaseline',
  'enable-background': 'enableBackground',
  'fill-opacity': 'fillOpacity',
  'fill-rule': 'fillRule',
  'flood-color': 'floodColor',
  'flood-opacity': 'floodOpacity',
  'font-family': 'fontFamily',
  'font-size': 'fontSize',
  'font-size-adjust': 'fontSizeAdjust',
  'font-stretch': 'fontStretch',
  'font-style': 'fontStyle',
  'font-variant': 'fontVariant',
  'font-weight': 'fontWeight',
  'glyph-name': 'glyphName',
  'glyph-orientation-horizontal': 'glyphOrientationHorizontal',
  'glyph-orientation-vertical': 'glyphOrientationVertical',
  'horiz-adv-x': 'horizAdvX',
  'horiz-origin-x': 'horizOriginX',
  'image-rendering': 'imageRendering',
  'letter-spacing': 'letterSpacing',
  'lighting-color': 'lightingColor',
  'marker-end': 'markerEnd',
  'marker-mid': 'markerMid',
  'marker-start': 'markerStart',
  'overline-position': 'overlinePosition',
  'overline-thickness': 'overlineThickness',
  'paint-order': 'paintOrder',
  'panose-1': 'panose1',
  'pointer-events': 'pointerEvents',
  'rendering-intent': 'renderingIntent',
  'shape-rendering': 'shapeRendering',
  'stop-color': 'stopColor',
  'stop-opacity': 'stopOpacity',
  'strikethrough-position': 'strikethroughPosition',
  'strikethrough-thickness': 'strikethroughThickness',
  'stroke-dasharray': 'strokeDasharray',
  'stroke-dashoffset': 'strokeDashoffset',
  'stroke-linecap': 'strokeLinecap',
  'stroke-linejoin': 'strokeLinejoin',
  'stroke-miterlimit': 'strokeMiterlimit',
  'stroke-opacity': 'strokeOpacity',
  'stroke-width': 'strokeWidth',
  'text-anchor': 'textAnchor',
  'text-decoration': 'textDecoration',
  'text-rendering': 'textRendering',
  'underline-position': 'underlinePosition',
  'underline-thickness': 'underlineThickness',
  'unicode-bidi': 'unicodeBidi',
  'unicode-range': 'unicodeRange',
  'units-per-em': 'unitsPerEm',
  'v-alphabetic': 'vAlphabetic',
  'v-hanging': 'vHanging',
  'v-ideographic': 'vIdeographic',
  'v-mathematical': 'vMathematical',
  'vector-effect': 'vectorEffect',
  'vert-adv-y': 'vertAdvY',
  'vert-origin-x': 'vertOriginX',
  'vert-origin-y': 'vertOriginY',
  'word-spacing': 'wordSpacing',
  'writing-mode': 'writingMode',
  'x-height': 'xHeight',
  'xlink:actuate': 'xlinkActuate',
  'xlink:arcrole': 'xlinkArcrole',
  'xlink:href': 'xlinkHref',
  'xlink:role': 'xlinkRole',
  'xlink:show': 'xlinkShow',
  'xlink:title': 'xlinkTitle',
  'xlink:type': 'xlinkType',
  'xml:base': 'xmlBase',
  'xml:lang': 'xmlLang',
  'xml:space': 'xmlSpace',
};

const processNode = (node, isRoot = false) => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.nodeValue.trim();
    return text ? text : '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const tagName = node.tagName.toLowerCase();

  // Filter out Inkscape/Sodipodi tags, metadata, and other non-rendering tags
  const blockedTags = ['metadata', 'defs', 'sodipodi:namedview', 'inkscape:grid', 'desc', 'title'];
  // Note: 'defs' is tricky. Usually we want it, but if it only contains inkscape trash it's annoying.
  // For now, let's KEEP 'defs' but rely on children filtering to clean it.

  if (tagName === 'metadata' || tagName === 'sodipodi:namedview' || tagName.includes('inkscape:') || tagName.includes('sodipodi:')) {
    return '';
  }

  let attrsString = '';

  // Process Attributes
  Array.from(node.attributes).forEach(attr => {
    let name = attr.name;
    const value = attr.value;

    // Filter namespaced attributes (e.g. inkscape:..., sodipodi:...)
    if (name.includes(':') && !name.startsWith('xlink:') && !name.startsWith('xml:')) {
      return;
    }

    // Explicitly filter standard namespaces that are not needed in JSX
    if (name.startsWith('xmlns:')) {
      // Keep xmlns:xlink if needed, but remove others
      if (name !== 'xmlns:xlink') return;
    }
    if (name === 'xmlns') return; // React doesn't need xmlns on root mostly, or it adds it auto.

    // Filter data attributes if strict
    if (name.startsWith('data-') && name !== 'data-name') return; // Keep data-name? Or remove all? Let's remove to be clean.

    // Map attribute name to React camelCase
    if (attrMap[name]) {
      name = attrMap[name];
    } else if (name.includes('-')) {
      // Fallback for unknown kebab-case props
      name = toCamelCase(name);
    }

    if (name === 'style') {
      const styleObj = convertStyle(value);
      if (styleObj && Object.keys(styleObj).length > 0) {
        attrsString += ` style={${JSON.stringify(styleObj)}}`;
      }
    } else {
      // Numeric value heuristic: if it looks like a number, pass as number?
      // React handles strings fine for almost everything.
      // But keeping it as string is safer mostly.
      attrsString += ` ${name}="${value}"`;
    }
  });

  // Inject Root Props
  if (isRoot && tagName === 'svg') {
    attrsString += ' ref={svgRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onClick={handleClick} style={{ width: "100%", height: "100%" }}';
  }

  // Recursive Children
  const children = Array.from(node.childNodes)
    .map(child => processNode(child))
    .join('');

  // If tag is defs and empty after cleaning, return empty
  if (tagName === 'defs' && !children.trim()) return '';

  if (children) {
    return `<${tagName}${attrsString}>\n${children}\n</${tagName}>`;
  } else {
    return `<${tagName}${attrsString} />`;
  }
};


function SvgEditor() {
  const [uploadedSvgContent, setUploadedSvgContent] = useState('');
  const [svgFileName, setSvgFileName] = useState('');
  const [svgElements, setSvgElements] = useState([]);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [hoveredInfo, pHoveredInfo] = useState({ id: null, pos: { x: null, y: null } });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  // Persistence State
  const [showPersistenceModal, setShowPersistenceModal] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null);

  const [config, setConfig] = useState({
    backgroundColor: '#ffffff',
    visualMode: 'none', // 'fill', 'border', or 'none'
    thresholdKey: 'value', // Which data key to apply thresholds to
    thresholds: [
      { type: 'numeric', min: 0, max: 2, color: '#4ade80', animation: 'none' },
      { type: 'numeric', min: 2, max: 5, color: '#facc15', animation: 'none' },
      { type: 'numeric', min: 5, max: 10, color: '#f97316', animation: 'pulse' },
    ],
    tooltipTemplate: 'Unit {{id}}: {{value}}',
    redirectUrl: '', // URL template for redirection on click
    navigationParams: [{ name: 'data', value: '{{row}}', type: 'auto' }],
    mappings: {},
  });

  const [dataSource, setDataSource] = useState({
    type: 'custom',
    data: [],
    url: '',
    method: 'GET'
  });

  const [valueByElementId, setValueByElementId] = useState(new Map());
  const [isLoadingApi, setIsLoadingApi] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  const showMessage = useCallback((msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('svgEditorSession');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.config) setConfig(parsed.config);
        if (parsed.dataSource) setDataSource(parsed.dataSource);
        if (parsed.svgContent) setUploadedSvgContent(parsed.svgContent);
        if (parsed.svgFileName) setSvgFileName(parsed.svgFileName);
        if (parsed.svgElements) setSvgElements(parsed.svgElements);
      }
    } catch (err) {
      console.error('Error loading session:', err);
    }
  }, []);

  useEffect(() => {
    const session = {
      config,
      dataSource,
      svgContent: uploadedSvgContent,
      svgFileName,
      svgElements,
    };
    localStorage.setItem('svgEditorSession', JSON.stringify(session));
  }, [config, dataSource, uploadedSvgContent, svgFileName, svgElements]);

  // Update mapping
  useEffect(() => {
    const newMap = new Map();
    const data = dataSource.data || [];

    // 1. Auto-mapping (lower priority)
    data.forEach((row) => {
      const elementId = String(row.id || row.elementId || row.flatId || row.unit || '');
      if (elementId) {
        newMap.set(elementId, row);
      }
    });

    // 2. Manual Overrides (higher priority)
    Object.entries(config.mappings || {}).forEach(([elementId, dataId]) => {
      // Logic: If mapped to empty string/null, it means EXPLICITLY UNMAPPED.
      // So we delete it from the map if it was auto-mapped.
      if (!dataId) {
        newMap.delete(elementId);
        return;
      }

      const row = data.find(r => String(r.id || r.elementId || r.flatId || r.unit) === String(dataId));
      if (row) {
        newMap.set(elementId, row);
      }
    });

    setValueByElementId(newMap);
  }, [dataSource.data, config.mappings]);

  const handleRemoveMapping = useCallback((elementId) => {
    setConfig(prev => ({
      ...prev,
      mappings: {
        ...prev.mappings,
        [elementId]: '' // Set to empty string to trigger unmap logic
      }
    }));
    showMessage(`Removed mapping for ${elementId}`, 'info');
  }, [showMessage]);

  const handleManualMap = (dataId) => {
    if (!selectedElementId) return;
    const newMappings = { ...config.mappings, [selectedElementId]: dataId };
    setConfig({ ...config, mappings: newMappings });
    showMessage(`Mapped ${selectedElementId} to data row ${dataId}`, 'success');
  };

  const handleSvgUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const svgText = e.target.result;
        setUploadedSvgContent(svgText);
        setSvgFileName(file.name);

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = svgText;
        const svgEl = tempDiv.querySelector('svg');

        if (svgEl) {
          const elements = Array.from(svgEl.querySelectorAll('[id]')).map((el) =>
            el.getAttribute('id')
          );
          setSvgElements(elements);
          showMessage(`SVG loaded: ${elements.length} elements detected`, 'success');
        } else {
          showMessage('Invalid SVG: No <svg> tag found', 'error');
        }
      } catch (err) {
        showMessage('Error parsing SVG: ' + err.message, 'error');
      }
    };
    reader.readAsText(file);
  };

  const handleAutoMap = async (action) => {
    if (action === 'fetch' && dataSource.type === 'api') {
      try {
        setIsLoadingApi(true);
        const headers = { 'Content-Type': 'application/json' };

        if (dataSource.authType === 'bearer' && dataSource.token) {
          headers['Authorization'] = `Bearer ${dataSource.token}`;
        } else if (dataSource.authType === 'basic' && dataSource.username) {
          const creds = btoa(`${dataSource.username}:${dataSource.password || ''}`);
          headers['Authorization'] = `Basic ${creds}`;
        }

        showMessage('Fetching data...', 'info');
        const response = await fetch(dataSource.url, {
          method: dataSource.method || 'GET',
          headers
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        // Handle both direct array or nested data property
        const dataArray = Array.isArray(result) ? result : (result.data || []);

        setDataSource({ ...dataSource, data: dataArray });
        setLastFetchTime(Date.now());
        setIsLoadingApi(false);
        showMessage(`Successfully fetched ${dataArray.length} items`, 'success');
        return;
      } catch (err) {
        setIsLoadingApi(false);
        showMessage(`Fetch failed: ${err.message}`, 'error');
        return;
      }
    }

    const data = dataSource.data || [];
    if (data.length === 0 || svgElements.length === 0) {
      showMessage('Upload SVG and Data first', 'warning');
      return;
    }

    const dataIds = data.map(row => String(row.id || row.elementId || row.flatId || row.unit || ''));
    const matchedCount = svgElements.filter(elId => dataIds.includes(elId)).length;

    showMessage(`⚡ Auto-mapped ${matchedCount} elements!`, 'success');
  };

  const exportProject = () => {
    const project = {
      config,
      dataSource,
      svgContent: uploadedSvgContent,
      svgFileName,
      version: '1.1'
    };
    const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `svg-mapper-${svgFileName || 'project'}.json`;
    a.click();
  };

  const importProject = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (re) => {
      try {
        const imported = JSON.parse(re.target.result);
        if (imported.config) setConfig(imported.config);
        if (imported.dataSource) setDataSource(imported.dataSource);
        if (imported.svgContent) setUploadedSvgContent(imported.svgContent);
        if (imported.svgFileName) setSvgFileName(imported.svgFileName);
        showMessage('Project imported successfully', 'success');
      } catch (err) {
        showMessage('Import failed: ' + err.message, 'error');
      }
    };
    reader.readAsText(file);
  };

  const generateReactComponent = () => {
    if (!uploadedSvgContent) {
      showMessage('Please upload an SVG first', 'error');
      return;
    }

    const componentName = (svgFileName || 'SvgComponent')
      .replace(/\.svg$/i, '')
      .replace(/[^a-zA-Z0-9]/g, '') || 'SvgComponent';

    const thresholdsJson = JSON.stringify(config.thresholds, null, 4);
    const mappingsJson = JSON.stringify(config.mappings, null, 4);

    // --- Generate JSX Logic ---
    let svgJsx = '';
    try {
      const parser = new DOMParser();
      // Handle potential parsing errors logic if needed, but DOMParser usually forgiving.
      const doc = parser.parseFromString(uploadedSvgContent, 'image/svg+xml');
      const svgEl = doc.querySelector('svg');
      if (svgEl) {
        svgJsx = processNode(svgEl, true);
      } else {
        throw new Error('No SVG element found during parsing.');
      }
    } catch (err) {
      console.error(err);
      showMessage('Error parsing SVG for React generation', 'error');
      return;
    }

    const code = `import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * ${componentName} - Dynamic SVG Component with Data Mapping
 * Generated by SVG Data Mapper
 */

const ${componentName} = ({ data = [], config: userConfig = {} }) => {
  const history = useHistory();
  const svgRef = useRef(null);
  const [valueByElementId, setValueByElementId] = useState(new Map());
  const [hoveredElement, setHoveredElement] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const castValue = (val, type) => {
    if (type === 'number') return Number(val);
    if (type === 'boolean') return val === 'true' || val === true;
    if (type === 'json') {
      try {
        return typeof val === 'string' ? JSON.parse(val) : val;
      } catch (e) {
        console.error('Error parsing JSON navigation param:', e);
        return val;
      }
    }
    if (type === 'string') return String(val);
    return val;
  };

  // Default configuration - memoized to prevent re-creation
  const defaultConfig = useMemo(() => ({
    visualMode: '${config.visualMode}',
    thresholdKey: '${config.thresholdKey || 'value'}',
    thresholds: ${thresholdsJson},
    tooltipTemplate: '${config.tooltipTemplate}',
    redirectUrl: '${config.redirectUrl || ''}',
    navigationParams: ${JSON.stringify(config.navigationParams || [])},
    mappings: ${mappingsJson},
  }), []);

  // Merge configs - memoized to prevent re-creation
  const mergedConfig = useMemo(() => ({
    ...defaultConfig,
    ...userConfig
  }), [defaultConfig, userConfig]);

  // Resolve threshold for a row - memoized with stable dependencies
  const resolveThreshold = useCallback((row) => {
    if (!row) return null;
    
    const thresholdKey = mergedConfig.thresholdKey || 'value';
    const value = row[thresholdKey];
    
    if (value == null) return null;
    
    for (const rule of mergedConfig.thresholds) {
      if (rule.type === 'string') {
        // String exact match
        if (String(value).toLowerCase() === String(rule.matchValue || '').toLowerCase()) {
          return rule;
        }
      } else {
        // Numeric range match (default)
        const numValue = Number(value);
        if (Number.isNaN(numValue)) continue;
        
        const minOk = rule.min == null || numValue >= rule.min;
        const maxOk = rule.max == null || numValue < rule.max;
        if (minOk && maxOk) return rule;
      }
    }
    return null;
  }, [mergedConfig.thresholds, mergedConfig.thresholdKey]);

  // Update element-to-data mapping
  useEffect(() => {
    const newMap = new Map();

    // Auto-mapping
    data.forEach(row => {
      const elId = String(row.id || row.elementId || row.flatId || row.unit || '');
      if (elId) newMap.set(elId, row);
    });

    // Manual overrides
    Object.entries(mergedConfig.mappings).forEach(([elId, dataId]) => {
      if (!dataId) {
        newMap.delete(elId);
        return;
      }
      const row = data.find(r => String(r.id || r.elementId || r.flatId || r.unit) === String(dataId));
      if (row) newMap.set(elId, row);
    });

    setValueByElementId(newMap);
  }, [data, mergedConfig.mappings]);

  const handleRemoveMapping = useCallback((id) => {
    setConfig(prev => ({
      ...prev,
      mappings: {
        ...prev.mappings,
        [id]: '' // Explicitly unmap
      }
    }));
  }, []);

  // Apply styles to SVG elements
  useEffect(() => {
    if (!svgRef.current) return;

    svgRef.current.querySelectorAll('[id]').forEach(el => {
      const id = el.getAttribute('id');
      const row = valueByElementId.get(id);
      const rule = resolveThreshold(row);
      const mode = mergedConfig.visualMode;

      // Reset styles
      el.style.transition = 'fill 0.3s, stroke 0.3s';
      
      if (row) {
        el.style.cursor = 'pointer';
        // Ensure element captures events even if it has no fill
        el.style.pointerEvents = 'all'; 
      } else {
        el.style.cursor = '';
        el.style.pointerEvents = '';
      }

      if (rule && mode !== 'none') {
        if (mode === 'fill') {
          el.style.fill = rule.color;
          el.style.fillOpacity = '0.7';
        } else if (mode === 'border') {
          el.style.stroke = rule.color;
          el.style.strokeWidth = '3px';
        }

        // Apply animation class
        if (rule.animation && rule.animation !== 'none') {
          el.classList.add('animate-' + rule.animation);
        }
      }
    });
  }, [valueByElementId, mergedConfig.visualMode, resolveThreshold]);

  // Handle mouse events for tooltip - memoized to prevent re-creation
  const handleMouseMove = useCallback((e) => {
    let current = e.target;
    let found = null;
    
    // Traverse upwards to find the nearest element that is actually mapped to data
    while (current && current !== svgRef.current) {
      if (current instanceof Element) {
         const id = current.getAttribute('id');
         if (id && valueByElementId.has(id)) {
           found = { id, row: valueByElementId.get(id) };
           break;
         }
      }
      current = current.parentElement;
    }

    if (found) {
      setHoveredElement(found);
      setTooltipPos({ x: e.clientX + 15, y: e.clientY + 15 });
    } else {
      setHoveredElement(null);
    }
  }, [valueByElementId]);

  // Handle mouse leave - memoized
  const handleMouseLeave = useCallback(() => {
    setHoveredElement(null);
  }, []);

  // Handle click events for redirection
  const handleClick = useCallback((e) => {
    if (!mergedConfig.redirectUrl) return;

    let current = e.target;
    let found = null;
    
    // Traverse upwards to find the nearest mapped element
    while (current && current !== svgRef.current) {
      if (current instanceof Element) {
         const id = current.getAttribute('id');
         if (id && valueByElementId.has(id)) {
           found = { id, row: valueByElementId.get(id) };
           break;
         }
      }
      current = current.parentElement;
    }

    if (found) {
       let url = mergedConfig.redirectUrl;
       url = url.replace(/{{id}}/g, found.id);
       const val = found.row.value ?? found.row.power_kw ?? '';
       url = url.replace(/{{value}}/g, val);
       
       Object.keys(found.row).forEach(key => {
         url = url.replace(new RegExp('{{' + key + '}}', 'g'), found.row[key] ?? '');
       });

       // Check if it's an external link or relative
       if (url) {
         if (url.startsWith('http') || url.startsWith('//')) {
           window.location.href = url;
         } else {
           const state = {};
           (mergedConfig.navigationParams || []).forEach(p => {
             const singleMatch = p.value.match(/^{{([^}]+)}}$/);
             let rawValue;

             if (singleMatch) {
               const field = singleMatch[1];
               if (field === 'row') {
                 rawValue = found.row;
               } else if (field === 'id') {
                 rawValue = found.row.id ?? found.row.elementId ?? found.row.flatId ?? found.row.unit ?? found.id;
               } else if (field === 'elementId') {
                 rawValue = found.id;
               } else {
                 rawValue = found.row[field] ?? '';
               }
             } else {
               let val = p.value;
               const dataId = found.row.id ?? found.row.elementId ?? found.row.flatId ?? found.row.unit ?? found.id;
               val = val.replace(/{{id}}/g, dataId);
               val = val.replace(/{{elementId}}/g, found.id);
               
               Object.keys(found.row).forEach(k => {
                  val = val.replace(new RegExp('{{' + k + '}}', 'g'), found.row[k] ?? '');
               });
               rawValue = val;
             }

             state[p.name] = p.type && p.type !== 'auto' ? castValue(rawValue, p.type) : rawValue;
           });
           
           history.push(url, state);
         }
       }
    }
  }, [valueByElementId, mergedConfig.redirectUrl, mergedConfig.navigationParams, history]);

  // Render tooltip text
  const getTooltipText = () => {
    if (!hoveredElement) return '';
    let text = mergedConfig.tooltipTemplate;
    text = text.replace(/{{id}}/g, hoveredElement.id);
    const val = hoveredElement.row.value ?? hoveredElement.row.power_kw ?? 'N/A';
    text = text.replace(/{{value}}/g, val);
    Object.keys(hoveredElement.row).forEach(key => {
      text = text.replace(new RegExp('{{' + key + '}}', 'g'), hoveredElement.row[key] ?? '');
    });
    return text.replace(/\\\\n/g, '\\n');
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style>{\`
        @keyframes pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-2px); }
          75% { transform: translateX(2px); }
        }
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        .animate-pulse { animation: pulse 1.5s ease-in-out infinite; }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; }
        .animate-flash { animation: flash 0.8s ease-in-out infinite; }
      \`}</style>
      
      ${svgJsx}

      {hoveredElement && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPos.x,
            top: tooltipPos.y,
            background: '#1e293b',
            color: '#f1f5f9',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 1000,
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
            whiteSpace: 'pre-line',
          }}
        >
          {getTooltipText()}
        </div>
      )}
    </div>
  );
};

export default ${componentName};

/*
 * Usage Example:
 * 
 * import ${componentName} from './${componentName}';
 * 
 * function App() {
 *   const [data, setData] = useState([
 *     { id: 'element1', value: 3 },
 *     { id: 'element2', value: 7 },
 *   ]);
 * 
 *   return <${componentName} data={data} />;
 * }
 */
`;

    setGeneratedCode(code);
    setShowCodeModal(true);
    showMessage('React component generated!', 'success');
  };

  const copyGeneratedCode = () => {
    navigator.clipboard.writeText(generatedCode).then(() => {
      showMessage('Code copied to clipboard!', 'success');
    });
  };

  const handleProjectLoad = (project) => {
    setCurrentProjectId(project.id);
    if (project.name) setSvgFileName(project.name); // Optional: Update displayed name
    if (project.config) setConfig(project.config);
    if (project.dataSource) setDataSource(project.dataSource);
    if (project.svgContent) {
      setUploadedSvgContent(project.svgContent);
      // Need to parse elements again
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = project.svgContent;
        const svgEl = tempDiv.querySelector('svg');
        if (svgEl) {
          const elements = Array.from(svgEl.querySelectorAll('[id]')).map((el) => el.getAttribute('id'));
          setSvgElements(elements);
        }
      } catch (e) {
        console.error("Error parsing SVG on load", e);
      }
    }
    // Handle mappings reconciliation if needed (assuming config.mappings is source of truth)
    showMessage(`Project "${project.name}" loaded`, 'success');
  };

  const handleSaveSuccess = (id) => {
    setCurrentProjectId(id);
    showMessage('Project saved successfully', 'success');
  };

  const downloadGeneratedCode = () => {
    const componentName = (svgFileName || 'SvgComponent')
      .replace(/\.svg$/i, '')
      .replace(/[^a-zA-Z0-9]/g, '') || 'SvgComponent';
    const blob = new Blob([generatedCode], { type: 'text/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${componentName}.jsx`;
    a.click();
  };

  return (
    <div className="app-container">
      <section className="canvas-section">
        <header className="canvas-header">
          <h2>{svgFileName || 'New Canvas'}</h2>

          <div style={{ display: 'flex', gap: '8px' }}>
            <label className="btn btn--secondary btn--sm">
              📂 Upload SVG
              <input type="file" accept=".svg" onChange={handleSvgUpload} style={{ display: 'none' }} />
            </label>
            <button className="btn btn--outline btn--sm" onClick={() => setShowPersistenceModal(true)}>☁️ Cloud Projects</button>
            <button className="btn btn--outline btn--sm" onClick={exportProject}>📤 Export</button>
            <label className="btn btn--outline btn--sm">
              📥 Import
              <input type="file" accept=".json" onChange={importProject} style={{ display: 'none' }} />
            </label>
            <button className="btn btn--primary btn--sm" onClick={generateReactComponent}>🚀 Generate React</button>
            <button className="btn btn--secondary btn--sm" style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #f87171' }} onClick={() => { if (window.confirm('Reset all?')) { localStorage.removeItem('svgEditorSession'); window.location.reload(); } }}>Reset</button>
          </div>
        </header>

        <div style={{ flex: 1, position: 'relative', }}>
          <BuildingSvgWrapper
            svgContent={uploadedSvgContent}
            config={config}
            selectedElementId={selectedElementId}
            onSelectElement={setSelectedElementId}
            valueByElementId={valueByElementId}
            svgElements={svgElements}
            onHover={(id, pos) => pHoveredInfo({ id, pos })}
            onHoverEnd={() => pHoveredInfo({ id: null, pos: { x: null, y: null } })}
            onRemoveMapping={handleRemoveMapping}
          />
        </div>

        {message && (
          <div className={`status status--${messageType}`} style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
            {message}
          </div>
        )}
      </section>

      <aside className="config-section" style={{ width: '400px', padding: '24px', height: '100vh', background: 'var(--color-surface)', borderLeft: '1px solid var(--color-border)' }}>
        <h1 style={{ fontSize: '20px', marginBottom: '24px' }}>SVG Data Mapper</h1>

        <DataSourceEditor
          dataSource={dataSource}
          onChange={setDataSource}
          onAutoMap={handleAutoMap}
        />

        <ApiDataViewer
          data={dataSource.data}
          isLoading={isLoadingApi}
          lastFetchTime={lastFetchTime}
          dataSourceType={dataSource.type}
        />

        <div className="sidebar-panel">
          <h3 style={{ marginBottom: '24px' }}>Element Configuration</h3>

          {/* Selected Element Section */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--color-border)' }}>
            <h4 style={{ fontSize: '14px', marginBottom: '12px', fontWeight: '600' }}>Selected Element</h4>
            {selectedElementId ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="status status--info" style={{ fontSize: '12px', padding: '8px', marginBottom: '4px' }}>
                  ID: <strong>{selectedElementId}</strong>
                </div>

                <label className="form-label" style={{ fontSize: '11px' }}>Map to Data Row:</label>
                <SearchableSelect
                  value={config.mappings?.[selectedElementId] || ''}
                  onChange={(value) => handleManualMap(value)}
                  placeholder="Search by ID or name..."
                  options={(dataSource.data || []).map((row, idx) => {
                    const label = row.label || row.name || row.id || row.elementId || row.unit || `Row ${idx + 1}`;
                    const id = row.id || row.elementId || row.flatId || row.unit || idx;
                    return { label: String(label), value: String(id) };
                  })}
                />
                <p style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                  Manual mapping overrides auto-mapping for this element.
                </p>
              </div>
            ) : (
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                Click an element on the SVG to map it.
              </p>
            )}
          </div>

          {/* Thresholds & Visuals Section */}
          <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid var(--color-border)' }}>
            <h4 style={{ fontSize: '14px', marginBottom: '12px', fontWeight: '600' }}>Thresholds & Visuals</h4>

            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '11px' }}>Threshold Data Key:</label>
              <select
                className="form-control"
                style={{ fontSize: '12px' }}
                value={config.thresholdKey || 'value'}
                onChange={(e) => setConfig({ ...config, thresholdKey: e.target.value })}
              >
                {(() => {
                  // Get unique keys from data
                  const keys = new Set();
                  (dataSource.data || []).forEach(row => {
                    Object.keys(row).forEach(key => keys.add(key));
                  });

                  // If no data, show default options
                  if (keys.size === 0) {
                    return (
                      <>
                        <option value="value">value</option>
                        <option value="power_kw">power_kw</option>
                        <option value="status">status</option>
                        <option value="temperature">temperature</option>
                        <option value="name">name</option>
                      </>
                    );
                  }

                  // Show actual data keys
                  return Array.from(keys).sort().map(key => (
                    <option key={key} value={key}>{key}</option>
                  ));
                })()}
              </select>
              <p style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                Select which data field to apply thresholds to
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ fontSize: '11px' }}>Visualization Mode:</label>
              <select
                className="form-control"
                style={{ fontSize: '12px' }}
                value={config.visualMode || 'none'}
                onChange={(e) => setConfig({ ...config, visualMode: e.target.value })}
              >
                <option value="none">Off</option>
                <option value="fill">Map (Fill)</option>
                <option value="border">Border</option>
              </select>
            </div>

            <ThresholdEditor
              thresholds={config.thresholds}
              onChangeThreshold={(idx, update) => {
                const newT = [...config.thresholds];
                newT[idx] = { ...newT[idx], ...update };
                setConfig({ ...config, thresholds: newT });
              }}
              onAdd={() => setConfig({ ...config, thresholds: [...config.thresholds, { type: 'numeric', min: 0, max: 10, color: '#00d4d4', animation: 'none' }] })}
              onRemove={(idx) => setConfig({ ...config, thresholds: config.thresholds.filter((_, i) => i !== idx) })}
            />
          </div>

          <div>
            <h4 style={{ fontSize: '14px', marginBottom: '12px', fontWeight: '600' }}>Tooltip Settings</h4>
            <label className="form-label">Template (use {"{{field}}"} or {"{{id}}"})</label>
            <input
              className="form-control"
              value={config.tooltipTemplate}
              onChange={(e) => setConfig({ ...config, tooltipTemplate: e.target.value })}
              placeholder="ID: {{id}} | Val: {{value}}"
            />
          </div>

          <div style={{ marginTop: '16px' }}>
            <label className="form-label">Redirect URL (Click Interaction)</label>
            <input
              className="form-control"
              value={config.redirectUrl || ''}
              onChange={(e) => setConfig({ ...config, redirectUrl: e.target.value })}
              placeholder="https://example.com/details/{{id}}"
            />
            <p style={{ fontSize: '10px', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
              Define a URL to redirect to when an element is clicked. Supports placeholders like {`{{id}}`}, {`{{value}}`}, etc.
            </p>
          </div>

          <div style={{ marginTop: '16px', borderTop: '1px solid var(--color-border)', paddingTop: '16px' }}>
            <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Navigation State Data</label>
            {(config.navigationParams || []).map((param, idx) => (
              <div key={idx} style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
                  <input
                    className="form-control"
                    style={{ flex: 1, fontSize: '11px' }}
                    placeholder="Key (e.g. id)"
                    value={param.name}
                    onChange={e => {
                      const newParams = [...(config.navigationParams || [])];
                      newParams[idx] = { ...newParams[idx], name: e.target.value };
                      setConfig({ ...config, navigationParams: newParams });
                    }}
                  />
                  <select
                    className="form-control"
                    style={{ width: '85px', fontSize: '10px', padding: '0 4px' }}
                    value={param.type || 'auto'}
                    onChange={e => {
                      const newParams = [...(config.navigationParams || [])];
                      newParams[idx] = { ...newParams[idx], type: e.target.value };
                      setConfig({ ...config, navigationParams: newParams });
                    }}
                  >
                    <option value="auto">Auto</option>
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="json">JSON</option>
                  </select>
                  <button
                    className="btn btn--secondary btn--sm"
                    style={{ padding: '0 6px', color: '#ef4444', height: '28px' }}
                    onClick={() => {
                      const newParams = config.navigationParams.filter((_, i) => i !== idx);
                      setConfig({ ...config, navigationParams: newParams });
                    }}
                  >✕</button>
                </div>
                <input
                  className="form-control"
                  style={{ width: '100%', fontSize: '11px' }}
                  placeholder="Value ({{row}} for all)"
                  value={param.value}
                  onChange={e => {
                    const newParams = [...(config.navigationParams || [])];
                    newParams[idx] = { ...newParams[idx], value: e.target.value };
                    setConfig({ ...config, navigationParams: newParams });
                  }}
                />
              </div>
            ))}
            <button
              className="btn btn--outline btn--sm"
              style={{ width: '100%', fontSize: '11px' }}
              onClick={() => setConfig({ ...config, navigationParams: [...(config.navigationParams || []), { name: '', value: '', type: 'auto' }] })}
            >
              + Add Data Parameter
            </button>
          </div>
        </div>

        <footer style={{ marginTop: '32px', fontSize: '11px', color: 'var(--color-text-secondary)' }}>
          <p>Mapped Elements: {valueByElementId.size} / {svgElements.length}</p>
          <p>Tip: Hold Alt + Left Click to pan. Ctrl + Scroll to zoom.</p>
        </footer>
      </aside>

      <TooltipOverlay
        visible={!!hoveredInfo.id}
        elementId={hoveredInfo.id}
        row={valueByElementId.get(hoveredInfo.id)}
        template={config.tooltipTemplate}
        x={hoveredInfo.pos.x}
        y={hoveredInfo.pos.y}
      />

      {/* Generated Code Modal */}
      {showCodeModal && (
        <div className="code-modal-overlay" onClick={() => setShowCodeModal(false)}>
          <div className="code-modal" onClick={(e) => e.stopPropagation()}>
            <div className="code-modal-header">
              <h3>Generated React Component</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn--primary btn--sm" onClick={copyGeneratedCode}>📋 Copy</button>
                <button className="btn btn--secondary btn--sm" onClick={downloadGeneratedCode}>💾 Download</button>
                <button className="btn btn--outline btn--sm" onClick={() => setShowCodeModal(false)}>✕</button>
              </div>
            </div>
            <pre className="code-modal-content">{generatedCode}</pre>
          </div>
        </div>
      )}

      <ProjectPersistenceModal
        isOpen={showPersistenceModal}
        onClose={() => setShowPersistenceModal(false)}
        currentProject={{
          config,
          dataSource,
          svgContent: uploadedSvgContent,
          mappings: config.mappings
        }}
        currentProjectId={currentProjectId}
        onLoad={handleProjectLoad}
        onSaveSuccess={handleSaveSuccess}
      />
    </div>
  );
}

export default SvgEditor;
